package diana.soleil.design.iraniancanadienbazzar.core.data

import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.model.Event
import kotlinx.coroutines.flow.Flow

/**
 * How long an event with no explicit `endsAt` is assumed to run. An organizer who left the end time
 * blank should not have their event disappear the minute it starts.
 */
const val DEFAULT_EVENT_DURATION_MILLIS: Long = 3L * 60 * 60 * 1_000

/**
 * Whether this event still counts as "upcoming" at [nowMillis] — i.e. it has not finished. An event
 * in progress is still upcoming; one that ended is not.
 *
 * Lives here, next to the interface that promises "upcoming", so the Firestore repository and the
 * fake cannot drift apart on what the word means (they had: neither filtered at all, and Home
 * happily showed events from weeks past).
 */
fun Event.isUpcomingAt(nowMillis: Long): Boolean {
    // Saturating add, not `startsAtMillis + DEFAULT_EVENT_DURATION_MILLIS`: a start time near
    // Long.MAX_VALUE (a corrupt or absurd timestamp) overflowed to a NEGATIVE end and the event was
    // judged long past — the far-future case failed exactly like the stale-event case this predicate
    // exists to fix.
    val end = endsAtMillis ?: if (startsAtMillis > Long.MAX_VALUE - DEFAULT_EVENT_DURATION_MILLIS) {
        Long.MAX_VALUE
    } else {
        startsAtMillis + DEFAULT_EVENT_DURATION_MILLIS
    }
    return end >= nowMillis
}

/** Public event discovery. Ticket purchase remains an organizer-site outlink until Phase 5. */
interface EventRepository {
    /**
     * Streams approved events that have not finished yet, optionally scoped to a launch metro,
     * soonest first. Past events are excluded — see [isUpcomingAt].
     */
    fun observeUpcomingEvents(metroId: String?): Flow<DataResult<List<Event>>>

    /** One-shot read (ticket claim header). */
    suspend fun getEvent(eventId: String): DataResult<Event>

    /** Streams the signed-in user's "I'm going" event ids (empty for guests). */
    fun observeMyRsvps(): Flow<Set<String>>

    /** Marks/unmarks the caller as going (dual write: public count mark + private mirror). */
    suspend fun setRsvp(eventId: String, going: Boolean): DataResult<Unit>

    /** One-shot attendee counts for the visible cards ({eventId: going}). Best-effort. */
    suspend fun rsvpCounts(eventIds: Collection<String>): Map<String, Int>
}
